import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, CalendarCheck, Wrench, IndianRupee, Award, FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fmtINR } from "@/lib/api";
import { TotpSettings } from "@/components/TotpSettings";
import { PrincipalBilling } from "@/components/admission/PrincipalBilling";

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
    <Icon className="w-5 h-5 text-[#FF2E63]" />
    <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">{label}</div>
    <div className="text-2xl font-extrabold text-[#0A192F] font-['Outfit']">{value}</div>
  </div>
);

const Kpis = ({ rep, prefix }) => {
  const t = rep.totals;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <Kpi icon={Users} label="Students" value={rep.student_count} testid={`${prefix}-students`} />
      <Kpi icon={CalendarCheck} label="Attendance" value={`${t.attendance_pct}%`} testid={`${prefix}-attendance`} />
      <Kpi icon={Wrench} label="Projects done" value={t.projects} testid={`${prefix}-projects`} />
      <Kpi icon={IndianRupee} label="Student earnings" value={fmtINR(t.earnings_inr)} testid={`${prefix}-earnings`} />
      <Kpi icon={Award} label="Certificates" value={t.certificates} testid={`${prefix}-certificates`} />
    </div>
  );
};

export default function PrincipalDashboard() {
  const nav = useNavigate();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem("rs_principal_token")}` } }), []);

  const load = useCallback(() => {
    api.get("/principal/dashboard", { ...auth, params: { month } }).then((r) => setD(r.data))
      .catch((e) => { if (e.response?.status === 401) { localStorage.removeItem("rs_principal_token"); nav("/principal/login"); } });
  }, [auth, month, nav]);

  useEffect(() => {
    if (!localStorage.getItem("rs_principal_token")) { nav("/principal/login"); return; }
    load();
  }, [load, nav]);

  const logout = () => { localStorage.removeItem("rs_principal_token"); nav("/principal/login"); };
  const pdfRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf().set({
        margin: [24, 24, 24, 24],
        filename: `RoboSpark-${d.principal.school_name.replace(/\s+/g, "-")}-${month}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, windowWidth: 1200, backgroundColor: "#f8fafc" },
        jsPDF: { unit: "pt", format: "a4", orientation: "landscape" },
        pagebreak: { mode: ["css", "avoid-all"] },
      }).from(pdfRef.current).save();
    } catch { toast.error("Could not create PDF"); } finally { setPdfBusy(false); }
  };

  return (
    <div className="bg-[#E8F0FE] min-h-screen" data-testid="principal-dashboard">
      <div ref={pdfRef} className="max-w-6xl mx-auto px-5 py-10 bg-[#E8F0FE]">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">RoboSpark · Principal dashboard</span>
            <h1 className="text-3xl font-extrabold text-[#0A192F]" data-testid="principal-school">{d?.principal.school_name || "…"}</h1>
            <p className="text-slate-600 text-sm">Welcome, {d?.principal.name} · Generated {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <div className="flex gap-2" data-html2canvas-ignore="true">
            <Button onClick={downloadPdf} disabled={!d || pdfBusy} className="rs-cta rounded-full" data-testid="principal-pdf-btn">
              {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileDown className="w-4 h-4 mr-1" />Download PDF</>}
            </Button>
            <Button variant="outline" onClick={logout} data-testid="principal-logout-btn"><LogOut className="w-4 h-4 mr-1" />Logout</Button>
          </div>
        </div>
        {d && (
          <>
            <h2 className="mt-8 mb-3 font-bold text-[#0A192F]">All time</h2>
            <Kpis rep={d.all_time} prefix="alltime" />
            <PrincipalBilling auth={auth} />
            <div className="mt-10 mb-3 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-bold text-[#0A192F]">Month view · {month}</h2>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-44 bg-white" data-testid="principal-month-input" data-html2canvas-ignore="true" />
            </div>
            <Kpis rep={d.month_report} prefix="month" />
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm" data-testid="principal-student-table">
                <thead className="bg-white text-left"><tr>{["Student", "Class", "Program", "Attendance (month)", "Projects (month)", "Earnings (month)", "All-time projects", "All-time earnings", "Certified"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
                <tbody>
                  {d.month_report.students.map((s, i) => {
                    const a = d.all_time.students[i];
                    return (
                      <tr key={i} className="border-t" data-testid={`principal-row-${i}`}>
                        <td className="px-3 py-2 font-semibold">{s.student_name}</td><td className="px-3">{s.class_grade}</td><td className="px-3">{s.program}</td>
                        <td className="px-3">{s.sessions ? `${s.present}/${s.sessions}` : "—"}</td><td className="px-3">{s.projects_completed}</td><td className="px-3">{fmtINR(s.earnings_inr)}</td>
                        <td className="px-3">{a?.projects_completed}</td><td className="px-3">{fmtINR(a?.earnings_inr)}</td><td className="px-3">{a?.certified ? "Yes" : "—"}</td>
                      </tr>
                    );
                  })}
                  {d.month_report.students.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-slate-500">No paid RoboSpark students found for your school yet.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-3">Data updates live as trainers mark attendance and projects. A monthly summary is also emailed on the 1st.</p>
            <div className="mt-8" data-html2canvas-ignore="true"><TotpSettings role="principal" /></div>
          </>
        )}
      </div>
    </div>
  );
}
